import { motion } from "framer-motion";
import { Zap, Play, ChevronRight, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteImages } from "@/context/SiteImagesContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { APP_REGISTER_URL } from "@/lib/config";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: "easeOut" } }),
};

export const Hero = () => {
  const { img } = useSiteImages();
  const { t } = useSiteContent();
  return (
    <section className="hero-glow grain relative overflow-hidden pt-36 pb-24 px-6" data-testid="hero-section">
      <div className="max-w-6xl mx-auto relative z-10 text-center">
        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={0}
          className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.05]"
        >
          {t('hero_title')} <span className="text-gradient">{t('hero_highlight')}</span>
        </motion.h1>

        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={1}
          className="mt-6 text-lg text-white/60 max-w-2xl mx-auto"
        >
          {t('hero_subtitle')}
        </motion.p>

        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="mt-12 grid lg:grid-cols-2 gap-5 max-w-4xl mx-auto text-left"
        >
          <div className="flex flex-col gap-4">
            <button
              data-testid="hero-card-editing"
              onClick={() => scrollTo("benefits")}
              className="glass rounded-2xl p-4 flex items-center gap-4 hover:border-indigo-400/40 transition-colors duration-300 group"
            >
              <img src={img('irisAmber')} alt="30-second iris editing" className="h-16 w-16 rounded-xl object-cover" />
              <span className="font-semibold text-white flex-1">{t('hero_card1')}</span>
              <ChevronRight className="h-5 w-5 text-white/40 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-card-art"
              onClick={() => scrollTo("templates")}
              className="glass rounded-2xl p-4 flex items-center gap-4 hover:border-violet-400/40 transition-colors duration-300 group"
            >
              <img src={img('art2')} alt="Free art generation" className="h-16 w-16 rounded-xl object-cover" />
              <span className="font-semibold text-white flex-1">{t('hero_card2')}</span>
              <ChevronRight className="h-5 w-5 text-white/40 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <button
            data-testid="watch-demo-button"
            onClick={() => scrollTo("features")}
            className="relative rounded-2xl overflow-hidden min-h-[200px] group border border-white/10"
          >
            <img src={img('heroEye')} alt="Watch demo" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
            <div className="relative z-10 h-full flex flex-col items-center justify-center gap-3">
              <span className="grid place-items-center h-16 w-16 rounded-full bg-white/90 group-hover:scale-110 transition-transform">
                <Play className="h-6 w-6 text-indigo-600 ml-1" fill="currentColor" />
              </span>
              <span className="text-white font-medium">{t('hero_demo_label')}</span>
            </div>
          </button>
        </motion.div>

        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={3}
          className="mt-12 flex flex-wrap items-center justify-center gap-4"
        >
          <Button
            data-testid="hero-trial-button"
            onClick={() => { window.location.href = APP_REGISTER_URL; }}
            className="rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white px-8 h-12 gap-2 text-base border-0 shadow-[0_8px_30px_rgba(99,102,241,0.4)]"
          >
            <Zap className="h-5 w-5" /> {t('hero_cta')}
          </Button>
          <Button
            data-testid="hero-templates-button"
            variant="outline"
            onClick={() => scrollTo("templates")}
            className="rounded-full h-12 px-8 bg-white/5 border-white/15 text-white hover:bg-white/10 gap-2 text-base"
          >
            <Palette className="h-5 w-5" /> {t('hero_explore')}
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
